import React, { useState, useEffect } from 'react';
import { DollarSign, Save, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';

const API_BASE_URL = 'https://aztrainersbackend.onrender.com/api';

interface PricingRates {
  rate_30_min: number;
  hourly_rate: number;
  rate_90_min: number;
  rate_120_min: number;
}

interface SessionType {
  duration: string;
  minutes: number;
  field: keyof PricingRates;
  description: string;
  icon: string;
}

const TrainerPricingManager: React.FC = () => {
  const [rates, setRates] = useState<PricingRates>({
    rate_30_min: 0,
    hourly_rate: 0,
    rate_90_min: 0,
    rate_120_min: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sessionTypes: SessionType[] = [
    {
      duration: '30 minutes',
      minutes: 30,
      field: 'rate_30_min',
      description: 'Quick consultation or focused training',
      icon: '⚡'
    },
    {
      duration: '1 hour',
      minutes: 60,
      field: 'hourly_rate',
      description: 'Standard personal training session',
      icon: '💪'
    },
    {
      duration: '1.5 hours',
      minutes: 90,
      field: 'rate_90_min',
      description: 'Extended training with warm-up/cool-down',
      icon: '🏃‍♂️'
    },
    {
      duration: '2 hours',
      minutes: 120,
      field: 'rate_120_min',
      description: 'Comprehensive training and assessment',
      icon: '🏋️‍♀️'
    }
  ];

  useEffect(() => {
    fetchCurrentRates();
  }, []);

  useEffect(() => {
    // Auto-clear messages after 5 seconds
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchCurrentRates = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/trainer/pricing/`, {
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRates({
          rate_30_min: data.rate_30_min || 0,
          hourly_rate: data.hourly_rate || 0,
          rate_90_min: data.rate_90_min || 0,
          rate_120_min: data.rate_120_min || 0
        });
      } else {
        setMessage({ type: 'error', text: 'Failed to load current rates' });
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
      setMessage({ type: 'error', text: 'Failed to load current rates' });
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (field: keyof PricingRates, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setRates(prev => ({
      ...prev,
      [field]: numericValue
    }));
    setHasChanges(true);
    setMessage(null); // Clear any existing messages when user starts editing
  };

  const validateRates = (): boolean => {
    // Check if all rates are positive numbers
    const rateValues = Object.values(rates);
    const hasNegative = rateValues.some(rate => rate < 0);
    const hasZero = rateValues.every(rate => rate === 0);

    if (hasNegative) {
      setMessage({ type: 'error', text: 'All rates must be positive numbers' });
      return false;
    }

    if (hasZero) {
      setMessage({ type: 'error', text: 'Please set at least one rate' });
      return false;
    }

    // Check for reasonable rate ranges (between $10-$500)
    const unreasonable = rateValues.some(rate => rate > 0 && (rate < 10 || rate > 500));
    if (unreasonable) {
      setMessage({ type: 'error', text: 'Rates should be between $10 and $500' });
      return false;
    }

    return true;
  };

  const calculateHourlyEquivalent = (rate: number, minutes: number): number => {
    return rate > 0 ? (rate / minutes) * 60 : 0;
  };

  const saveRates = async () => {
    if (!validateRates()) {
      return;
    }

    setSaving(true);
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/trainer/pricing/`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rates),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Pricing updated successfully!' });
        setHasChanges(false);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to update pricing' });
      }
    } catch (error) {
      console.error('Error saving rates:', error);
      setMessage({ type: 'error', text: 'Failed to update pricing' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading pricing information...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Session Pricing</h3>
          <p className="text-sm text-gray-600">Set your rates for different session lengths</p>
        </div>
        <div className="flex items-center space-x-3">
          {hasChanges && (
            <span className="text-sm text-orange-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              Unsaved changes
            </span>
          )}
          <button
            onClick={saveRates}
            disabled={saving || !hasChanges}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              saving || !hasChanges
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Pricing Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sessionTypes.map((session) => {
          const currentRate = rates[session.field];
          const hourlyEquivalent = calculateHourlyEquivalent(currentRate, session.minutes);
          
          return (
            <div key={session.field} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{session.icon}</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{session.duration}</h4>
                    <p className="text-sm text-gray-600">{session.description}</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-500">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="text-sm">{session.minutes}min</span>
                </div>
              </div>

              <div className="space-y-4">
                {/* Rate Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Rate
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={currentRate || ''}
                      onChange={(e) => handleRateChange(session.field, e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Hourly Equivalent */}
                {currentRate > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-600">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        <span>Hourly equivalent:</span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        ${hourlyEquivalent.toFixed(2)}/hr
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pricing Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-3">Pricing Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sessionTypes.map((session) => {
            const rate = rates[session.field];
            return (
              <div key={session.field} className="text-center">
                <div className="text-lg">{session.icon}</div>
                <div className="text-sm text-gray-600">{session.duration}</div>
                <div className="font-semibold text-gray-900">
                  {rate > 0 ? `$${rate.toFixed(2)}` : 'Not set'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h5 className="font-medium text-gray-900 mb-2">Pricing Tips</h5>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Consider offering discounts for longer sessions to encourage bookings</li>
          <li>• Research competitor rates in your area for competitive pricing</li>
          <li>• You can adjust rates anytime, but existing bookings keep their original price</li>
          <li>• Higher rates for longer sessions account for prep time and deeper training</li>
          <li>• Please remember there is a $5 platform fee. So if you want to make $30 for a session, price it at $35</li>
        </ul>
      </div>
    </div>
  );
};

export default TrainerPricingManager;